import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from './ui/alert-dialog';
import { MapPin, Plus, Pencil, Trash2, Navigation, Building2 } from 'lucide-react';
import { apiListWorkplaces, apiCreateWorkplace, apiUpdateWorkplace, apiDeleteWorkplace } from '../api';
import { toast } from 'sonner';
import { logError } from '../lib/logger';

const emptyForm = { id: null, name: '', address: '', lat: '', lng: '', radius: 100, active: true };

const WorkplaceForm = ({ initial, onSubmit, onCancel }) => {
  const [form, setForm] = useState(initial || emptyForm);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Inserisci il nome della sede'); return; }
    if (!form.address.trim()) { toast.error('Inserisci l\'indirizzo'); return; }
    const lat = parseFloat(form.lat);
    const lng = parseFloat(form.lng);
    if (isNaN(lat) || lat < -90 || lat > 90) { toast.error('Latitudine non valida (-90 / +90)'); return; }
    if (isNaN(lng) || lng < -180 || lng > 180) { toast.error('Longitudine non valida (-180 / +180)'); return; }
    const radius = parseInt(form.radius) || 100;
    if (radius < 10 || radius > 5000) { toast.error('Raggio deve essere tra 10m e 5000m'); return; }
    onSubmit({ ...form, lat, lng, radius });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="wname">Nome sede <span className="text-red-500">*</span></Label>
        <Input id="wname" placeholder="es. HC Milano" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
      </div>
      <div className="space-y-2">
        <Label htmlFor="waddr">Indirizzo <span className="text-red-500">*</span></Label>
        <Input id="waddr" placeholder="Via, numero, CAP, città" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="wlat">Latitudine <span className="text-red-500">*</span></Label>
          <Input id="wlat" type="number" step="any" placeholder="45.4642" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="wlng">Longitudine <span className="text-red-500">*</span></Label>
          <Input id="wlng" type="number" step="any" placeholder="9.1900" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} required />
        </div>
      </div>
      <p className="text-[11px] text-slate-500 -mt-1">Tip: apri Google Maps, clicca con il tasto destro sul punto esatto e copia le coordinate</p>
      <div className="space-y-2">
        <Label htmlFor="wrad">Raggio di tolleranza (metri)</Label>
        <Input id="wrad" type="number" min="10" max="5000" value={form.radius} onChange={(e) => setForm({ ...form, radius: e.target.value })} />
      </div>
      <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
        <div>
          <div className="text-sm font-medium">Sede attiva</div>
          <div className="text-xs text-slate-500">Se disattivata, nessuno può timbrare da questa sede</div>
        </div>
        <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Annulla</Button>
        <Button type="submit" className="bg-blue-700 hover:bg-blue-800">
          {form.id ? 'Salva modifiche' : 'Aggiungi sede'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const WorkplaceManager = () => {
  const [workplaces, setWorkplaces] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    apiListWorkplaces().then(setWorkplaces).catch(() => setWorkplaces([]));
  }, []);

  const reload = async () => {
    try { setWorkplaces(await apiListWorkplaces()); }
    catch (e) { logError('Failed to load workplaces:', e); }
  };

  const handleAdd = () => { setEditing(null); setDialogOpen(true); };
  const handleEdit = (wp) => { setEditing(wp); setDialogOpen(true); };

  const handleSubmit = async (data) => {
    try {
      if (data.id) {
        const { id, createdAt, created_at, ...payload } = data;
        const updated = await apiUpdateWorkplace(id, payload);
        setWorkplaces(prev => prev.map(w => w.id === id ? updated : w));
        toast.success('Sede aggiornata');
      } else {
        const { id, createdAt, created_at, ...payload } = data;
        const created = await apiCreateWorkplace(payload);
        setWorkplaces(prev => [...prev, created]);
        toast.success('Sede aggiunta');
      }
      setDialogOpen(false);
      setEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await apiDeleteWorkplace(toDelete.id);
      setWorkplaces(prev => prev.filter(w => w.id !== toDelete.id));
      toast.success('Sede eliminata');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore eliminazione');
    }
    setToDelete(null);
  };

  const toggleActive = async (wp) => {
    try {
      const { id, createdAt, created_at, ...payload } = wp;
      const updated = await apiUpdateWorkplace(id, { ...payload, active: !wp.active });
      setWorkplaces(prev => prev.map(w => w.id === id ? updated : w));
    } catch (err) {
      toast.error('Errore aggiornamento');
    }
  };

  const openMaps = (wp) => {
    window.open(`https://www.google.com/maps?q=${wp.lat},${wp.lng}`, '_blank');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">Sedi aziendali</div>
          <div className="text-xs text-slate-500">Gestisci le posizioni da cui i dipendenti possono timbrare</div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button onClick={handleAdd} className="bg-blue-700 hover:bg-blue-800 gap-2">
              <Plus className="w-4 h-4" /> Aggiungi sede
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Modifica sede' : 'Nuova sede aziendale'}</DialogTitle>
              <DialogDescription>
                I dipendenti potranno timbrare solo se si trovano entro il raggio configurato.
              </DialogDescription>
            </DialogHeader>
            <WorkplaceForm initial={editing} onSubmit={handleSubmit} onCancel={() => { setDialogOpen(false); setEditing(null); }} />
          </DialogContent>
        </Dialog>
      </div>

      {workplaces.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300">
          <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-sm font-medium text-slate-700">Nessuna sede configurata</div>
          <p className="text-xs text-slate-500 mt-1">Aggiungi la prima sede aziendale per abilitare le timbrature</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {workplaces.map(wp => (
            <div key={wp.id} className={`p-4 rounded-xl border bg-white ${wp.active ? 'border-slate-200' : 'border-slate-200 opacity-70'}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-start gap-2.5 min-w-0 flex-1">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${wp.active ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
                    <Building2 className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm text-slate-900 truncate">{wp.name}</div>
                    <div className="text-xs text-slate-500 truncate">{wp.address}</div>
                  </div>
                </div>
                <div className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${wp.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                  {wp.active ? 'Attiva' : 'Disattivata'}
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] text-slate-600 font-mono bg-slate-50 rounded px-2 py-1 mb-3">
                <MapPin className="w-3 h-3" />
                {wp.lat.toFixed(6)}, {wp.lng.toFixed(6)}
                <span className="text-slate-400 ml-auto">Raggio: {wp.radius}m</span>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={() => openMaps(wp)}>
                  <Navigation className="w-3 h-3" /> Mappa
                </Button>
                <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={() => handleEdit(wp)}>
                  <Pencil className="w-3 h-3" /> Modifica
                </Button>
                <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={() => toggleActive(wp)}>
                  {wp.active ? 'Disattiva' : 'Attiva'}
                </Button>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 ml-auto" onClick={() => setToDelete(wp)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la sede?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare <strong>{toDelete?.name}</strong>. Le timbrature esistenti rimarranno nel sistema. Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WorkplaceManager;
